import { Col, Form, Input, Row, Select, Spin, Steps } from "antd";
import { useForm } from "antd/es/form/Form";
import React, { useContext, useEffect, useState } from "react";
import { adminContext } from "../../Common/PageRoute";
import { useNavigate } from "react-router-dom";
import PracticesService from "../../Services/PracticesService";
import {
  LoadingOutlined,
} from "@ant-design/icons";
import { Utils } from "../../Utilities/Utils";
import { SAModal } from "../../Common/Custom Components/Ant design/SAModal";
import { IPractices, usePractice } from "../../Context/PracticeContext";

const AddPractice = ({
  onDismiss,
}: {
  onDismiss: (data?: any) => void;
}) => {
  const [form] = useForm();
  const navigate = useNavigate();
  const { openNotification } = useContext(adminContext);
  const [isCloneSelected, setIsCloneSelected] = useState(false); // State to toggle confirmation message
  const [loading, setLoading] = React.useState(false);
  const { allPracticeIds, addPractice } = usePractice();

  const [cloningMode, setCloningMode] = useState(false);
  const [cloneProgress, setCloneProgress] = useState<any>(null);
  const [cloneError, setCloneError] = useState<string | null>(null);
  const [cloneElapsedTime, setCloneElapsedTime] = useState(0);

  const startPollingProgress = (newId: number) => {
    let intervalId = setInterval(async () => {
      try {
        let response = await PracticesService.getCloneProgress(newId);
        if (response.status && response.result) {
          const progress = response.result;
          setCloneProgress(progress);

          if (progress.status === "Completed") {
            clearInterval(intervalId);
            openNotification("success", "Practice created and cloned successfully.");
            setTimeout(() => {
              navigate(`/practices/${newId}`);
              onDismiss(true);
            }, 1500);
          } else if (progress.status === "Failed") {
            clearInterval(intervalId);
            setCloneError(progress.errorMessage || "Cloning failed.");
            openNotification("error", progress.errorMessage || "Cloning failed.");
          }
        }
      } catch (err: any) {
        // Continue polling
      }
    }, 2000);
  };

  useEffect(() => {
    if (!cloningMode || cloneError) {
      return;
    }

    const timer = window.setInterval(() => {
      setCloneElapsedTime((prev) => prev + 1);
    }, 1000);

    return () => window.clearInterval(timer);
  }, [cloningMode, cloneError]);

  const formatElapsedTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
      .toString()
      .padStart(2, "0");
    const secs = (seconds % 60).toString().padStart(2, "0");
    return `${mins}:${secs}`;
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setCloneError(null);
      setCloneElapsedTime(0);
      const value = await form.validateFields();
      const cloneId = value?.clonePractice;
      let res = await PracticesService.addPractices(value, cloneId);
      if (res.status) {
        addPractice({
          value: res?.result?.id,
          label: res?.result?.name,
        });

        if (cloneId > 0) {
          setCloningMode(true);
          setCloneProgress({
            status: "Processing",
            phases: [
              { name: "Core Setup", status: "Completed", message: "Core setup completed." },
              { name: "Settings & Sequences", status: "Pending", message: "Pending setup." },
              { name: "Finance & Mappings", status: "Pending", message: "Pending setup." },
              { name: "CRM & Lead Rules", status: "Pending", message: "Pending setup." },
              { name: "Templates & Communications", status: "Pending", message: "Pending setup." },
              { name: "Tasks & Note Types", status: "Pending", message: "Pending setup." }
            ]
          });
          startPollingProgress(res?.result?.id);
        } else {
          navigate(`/practices/${res?.result?.id}`);
          openNotification("success", "Practice added successfully.");
          onDismiss(true);
        }
      } else {
        openNotification("error", res.message);
        setLoading(false);
      }
    } catch (error: any) {
      setLoading(false);
      if (error?.errorFields) {
        // Validation error
      } else {
        openNotification("error", "Failed to add practice");
      }
    }
  };

  const getCurrentStepIndex = () => {
    if (!cloneProgress || !cloneProgress.phases) return 0;
    const index = cloneProgress.phases.findIndex((p: any) => p.status === "Processing" || p.status === "Pending" || p.status === "Failed");
    return index >= 0 ? index : cloneProgress.phases.length - 1;
  };

  const getCurrentPhase = () => {
    if (!cloneProgress || !cloneProgress.phases) return null;
    const index = getCurrentStepIndex();
    return cloneProgress.phases[index] || null;
  };

  return (
    <SAModal
      open={true}
      width={600}
      title={cloningMode ? "Cloning practice modules" : "Add practice"}
      okText="Save"
      cancelText={cloneError ? "Close" : "Cancel"}
      isLoading={loading}
      onCancel={() => onDismiss(false)}
      onOk={handleSave}
      okButtonProps={cloningMode ? { style: { display: "none" } } : undefined}
      footer={cloningMode ? (cloneError ? undefined : null) : undefined}
    >
      <Spin
        spinning={loading && !cloningMode}
        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
      >
        {cloningMode ? (
          <div style={{ padding: "16px 24px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "12px",
                color: "#1677ff",
                fontWeight: 600,
              }}
            >
              <LoadingOutlined spin style={{ fontSize: 16 }} />
              <span>
                {getCurrentPhase()?.name
                  ? `Working on ${getCurrentPhase().name}`
                  : "Preparing clone..."}
              </span>
              <span style={{ color: "#666", fontWeight: 500 }}>
                {formatElapsedTime(cloneElapsedTime)}
              </span>
            </div>
            <Steps
              direction="vertical"
              size="small"
              current={getCurrentStepIndex()}
              status={cloneError ? "error" : "process"}
              items={(cloneProgress?.phases || []).map((phase: any) => {
                let status: "wait" | "process" | "finish" | "error" = "wait";
                if (phase.status === "Completed") status = "finish";
                else if (phase.status === "Processing") status = "process";
                else if (phase.status === "Failed") status = "error";

                return {
                  title: phase.name,
                  description:
                    phase.status === "Processing"
                      ? `${phase.message} (Loading... ${formatElapsedTime(cloneElapsedTime)})`
                      : phase.message,
                  status: status,
                  icon:
                    phase.status === "Processing" ? (
                      <LoadingOutlined spin style={{ fontSize: 16 }} />
                    ) : undefined,
                };
              })}
            />
            {cloneError && (
              <div style={{ marginTop: "20px", color: "#ff4d4f", fontWeight: "bold" }}>
                Error: {cloneError}
              </div>
            )}
          </div>
        ) : (
          <Form form={form}>
            <Row gutter={[10, 10]}>
              <Col xs={24} sm={12} lg={12}>
                <Form.Item
                  className="custom-form-item"
                  labelCol={{ span: 24 }}
                  wrapperCol={{ span: 24 }}
                  label="Clone practice"
                  name="clonePractice"
                >
                  <Select
                    showSearch
                    className="select-height-28"
                    options={allPracticeIds.map((p: IPractices) => ({
                      value: p.value,
                      label: p.label + " (ID: " + p.value + ")",
                    }))}
                    onChange={(value) => setIsCloneSelected(!!value)} // Update state when an option is selected
                    placeholder="Select a practice to clone from"
                    title="Select a practice to clone from"
                    allowClear
                    filterOption={(input: any, option: any) => {
                      return option?.label
                        ?.toLowerCase()
                        .includes(input.toLowerCase());
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={12}>
                <Form.Item
                  className="custom-form-item"
                  labelCol={{ span: 24 }}
                  wrapperCol={{ span: 24 }}
                  label="Practice name"
                  name="name"
                  rules={[
                    { required: true, message: "Name is required" },
                    { validator: Utils.validateWhiteSpace },
                  ]}
                >
                  <Input className="line-height-28" placeholder="Name" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[10, 10]}>
              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  className="custom-form-item"
                  labelCol={{ span: 24 }}
                  wrapperCol={{ span: 24 }}
                  label="Title"
                  name={["userName", "title"]}
                >
                  <Select
                    disabled={false}
                    className="select-height-28"
                    placeholder="Title"
                    options={[
                      { value: "Mr", label: "Mr" },
                      { value: "Mrs", label: "Mrs" },
                      { value: "Miss", label: "Miss" },
                      { value: "Sir", label: "Sir" },
                      { value: "Ms", label: "Ms" },
                      { value: "Dr", label: "Dr" },
                      { value: "Prof", label: "Prof" },
                      { value: "Madam", label: "Madam" },
                      { value: "Ma'am", label: "Ma'am" },
                      { value: "Lord", label: "Lord" },
                      { value: "Lady", label: "Lady" },
                      { value: "Reverend", label: "Reverend" },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  className="custom-form-item"
                  labelCol={{ span: 24 }}
                  wrapperCol={{ span: 24 }}
                  label="First name"
                  name={["userName", "first"]}
                  rules={[
                    { required: true, message: "First name is required" },
                    { validator: Utils.validateWhiteSpace },
                  ]}
                >
                  <Input
                    disabled={false}
                    className="line-height-28"
                    placeholder="First name"
                    onKeyPress={(e: any) => Utils.ValidateCharOnly(e)}
                    onInput={(e: any) =>
                    (e.target.value =
                      e.target.value.length > 1
                        ? e.target.value
                        : e.target.value.toUpperCase())
                    }
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  className="custom-form-item"
                  labelCol={{ span: 24 }}
                  wrapperCol={{ span: 24 }}
                  label="Last name"
                  name={["userName", "last"]}
                  rules={[
                    { required: true, message: "Last name is required" },
                    { validator: Utils.validateWhiteSpace },
                  ]}
                >
                  <Input
                    disabled={false}
                    className="line-height-28"
                    placeholder="Last name"
                    onKeyPress={(e: any) => Utils.ValidateCharOnly(e)}
                    onInput={(e: any) =>
                    (e.target.value =
                      e.target.value.length > 1
                        ? e.target.value
                        : e.target.value.toUpperCase())
                    }
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[10, 10]}>
              <Col xs={24} sm={12}>
                <Form.Item
                  className="custom-form-item"
                  labelCol={{ span: 24 }}
                  wrapperCol={{ span: 24 }}
                  label="Email"
                  name="email"
                  rules={[
                    { required: true, message: "Email is required" },
                    { validator: Utils.ValidateEmailFormat },
                  ]}
                >
                  <Input className="line-height-28" placeholder="Email" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  className="custom-form-item"
                  labelCol={{ span: 24 }}
                  wrapperCol={{ span: 24 }}
                  label="Host"
                  name="host"
                  rules={[
                    { required: true, message: "Host is required" },
                    { validator: Utils.validateWhiteSpace },
                  ]}
                >
                  <Input className="line-height-28" placeholder="Host" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  className="custom-form-item"
                  labelCol={{ span: 24 }}
                  wrapperCol={{ span: 24 }}
                  label="Database name"
                  name="database"
                  rules={[
                    { required: true, message: "Database is requried" },
                    { validator: Utils.validateWhiteSpace },
                  ]}
                >
                  <Input className="line-height-28" placeholder="Database name" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  className="custom-form-item"
                  labelCol={{ span: 24 }}
                  wrapperCol={{ span: 24 }}
                  label="Identifier"
                  name="identifier"
                  rules={[
                    { required: true, message: "Identifier is required" },
                    { validator: Utils.validateWhiteSpace },
                  ]}
                >
                  <Input className="line-height-28" placeholder="Identifier" />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <Form.Item
                  className="custom-form-item"
                  labelCol={{ span: 24 }}
                  wrapperCol={{ span: 24 }}
                  label="Connection string"
                  name="connectionString"
                  dependencies={["database"]}
                  rules={[
                    { required: true, message: "Connection string is required" },
                    { validator: Utils.validateWhiteSpace },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const dbName = getFieldValue("database");

                        if (!value || !dbName) {
                          return Promise.resolve();
                        }

                        const match = value.match(/\.net\/([^?]+)/);

                        if (!match) {
                          return Promise.reject(
                            new Error("Invalid MongoDB connection string format"),
                          );
                        }

                        const dbFromConnection = match[1];

                        if (dbFromConnection === dbName) {
                          return Promise.resolve();
                        }

                        return Promise.reject(
                          new Error(
                            `Database name mismatch. Expected "${dbName}" but found "${dbFromConnection}"`,
                          ),
                        );
                      },
                    }),
                  ]}
                >
                  <Input
                    className="line-height-28"
                    placeholder="Connection string"
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <Form.Item
                  className="custom-form-item"
                  labelCol={{ span: 24 }}
                  wrapperCol={{ span: 24 }}
                  label="Company logo URL"
                  name="logoUrl"
                >
                  <Input
                    className="line-height-28"
                    placeholder="Company logo URL"
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        )}
      </Spin>

      {isCloneSelected && !cloningMode && (
        <Row className="px-3 py-1">
          <Col
            span={24}
            className="p-3"
            style={{ backgroundColor: "#eef2ff", borderRadius: 5 }}
          >
            <Row className="font-bold">Are you sure?</Row>
            <Row>You want to clone details from the selected practice.</Row>
          </Col>
        </Row>
      )}
    </SAModal>
  );
};

export default AddPractice;
